import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Check, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${window.location.origin}/api`;

export default function JoinGroup({ inviteCode }) {
  const [groupInfo, setGroupInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [joined, setJoined] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGroupInfo = async () => {
      try {
        const res = await fetch(`${API}/invite/${inviteCode}`);
        if (res.ok) {
          setGroupInfo(await res.json());
        } else {
          setError("This invite link is invalid or has expired.");
        }
      } catch {
        setError("Failed to load invite. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchGroupInfo();
  }, [inviteCode]);

  const handleJoin = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Please enter your name and email");
      return;
    }

    setJoining(true);
    try {
      const res = await fetch(`${API}/invite/${inviteCode}/join`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });

      if (res.ok) {
        const data = await res.json();
        if (data.already_member) {
          toast.info("You're already a member of this group!");
        } else {
          toast.success("Successfully joined!");
        }
        setJoined(true);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to join");
      }
    } catch {
      toast.error("Failed to join. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1d21]">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1d21] px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">Invalid Invite</h1>
          <p className="text-gray-400">{error}</p>
          <Button
            className="mt-6 bg-blue-500 hover:bg-blue-600"
            onClick={() => { window.location.hash = ""; window.location.href = "/"; }}
          >
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  if (joined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1d21] px-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-400" />
          </div>
          <h1 className="text-xl font-semibold text-white mb-2">You're in!</h1>
          <p className="text-gray-400">
            You've joined <span className="text-white font-medium">{groupInfo?.group_name}</span>
          </p>
          <Button
            className="mt-6 bg-blue-500 hover:bg-blue-600"
            onClick={() => { window.location.hash = ""; window.location.href = "/"; }}
          >
            Go to App
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1a1d21] px-4" data-testid="join-group-page">
      <div className="w-full max-w-sm">
        {/* Group Info */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-blue-400" />
          </div>
          <h1 className="text-xl font-semibold text-white mb-1">
            Join {groupInfo?.group_name}
          </h1>
          <p className="text-sm text-gray-400">
            Invited by {groupInfo?.owner_name} · {groupInfo?.member_count} member{groupInfo?.member_count !== 1 ? "s" : ""}
          </p>
        </div>

        {/* Join Form */}
        <div className="bg-[#1e2126] rounded-xl border border-white/10 p-6 space-y-4">
          <div>
            <label className="text-sm text-gray-400 mb-1.5 block">Your Name</label>
            <Input
              data-testid="join-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your full name"
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
          </div>
          <div>
            <label className="text-sm text-gray-400 mb-1.5 block">Your Email</label>
            <Input
              data-testid="join-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="bg-white/5 border-white/10 text-white placeholder:text-gray-500"
              onKeyDown={(e) => { if (e.key === "Enter") handleJoin(); }}
            />
          </div>
          <Button
            data-testid="join-btn"
            className="w-full bg-blue-500 hover:bg-blue-600"
            onClick={handleJoin}
            disabled={joining}
          >
            {joining ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            {joining ? "Joining..." : "Join Group"}
          </Button>
        </div>

        <p className="text-xs text-gray-600 text-center mt-4">
          By joining, you'll be added as a member of this group.
        </p>
      </div>
    </div>
  );
}
